import { React, PropTypes } from 'imports';
import ReactDOM from 'react-dom';
import TimeStamp from '../TimeStamp/TimeStamp';
import { PeriodType } from 'utils/constants';
import { deepEqual, deepClone, deepAssign, deepDiff, toArray } from 'utils';
import Enum from 'utils/Enum';
import common from '!!less-vars-loader!static/common.less';
import './Chart.less';


function assignByPath(obj, path, value) {
    if (path === '') {
        return value;
    }
    else if (path[0] === '[') {
        obj = obj || [];
        let result = Object.assign(Array.isArray(obj) ? [] : {}, obj),
            keyParts = /^\[([^\]]+)\]/.exec(path),
            key = keyParts && keyParts[1];
        if (key[0] === '"' && key[key.length - 1] === '"' || key[0] === "'" && key[key.length - 1] === "'") {
            key = key.slice(1, -1);
        }
        if (!key) {
            throw new Error(`Can not parse path: '${path}'.`);
        }
        result[key] = assignByPath(result[key], path.slice(keyParts[0].length), value);
        return result;
    } else if (path[0] === '.') {
        obj = obj || {};
        let result = Object.assign(Array.isArray(obj) ? [] : {}, obj),
            keyParts = /^\.([^\[\.]+)/.exec(path),
            key = keyParts && keyParts[1];
        if (!key) {
            throw new Error(`Can not parse path: '${path}'.`);
        }
        result[key] = assignByPath(result[key], path.slice(keyParts[0].length), value);
        return result;
    }
    else {
        throw new Error(`Can not parse path: '${path}'.`);
    }
};

function getByPath(obj, path) {
    if (path === '') {
        return obj;
    }
    else if (path[0] === '[') {
        obj = obj || [];
        let keyParts = /^\[([^\]]+)\]/.exec(path),
            key = keyParts && keyParts[1];
        if (key[0] === '"' && key[key.length - 1] === '"' || key[0] === "'" && key[key.length - 1] === "'") {
            key = key.slice(1, -1);
        }
        if (!key) {
            throw new Error(`Can not parse path: '${path}'.`);
        }
        return getByPath(obj[key], path.slice(keyParts[0].length));
    } else if (path[0] === '.') {
        obj = obj || {};
        let keyParts = /^\.([^\[\.]+)/.exec(path),
            key = keyParts && keyParts[1];
        if (!key) {
            throw new Error(`Can not parse path: '${path}'.`);
        }
        return getByPath(obj[key], path.slice(keyParts[0].length));
    }
    else {
        throw new Error(`Can not parse path: '${path}'.`);
    }
};

// we can not use 'import' here since webpack tries to evaluate all imported modules as soon as current module is imported somewhere
// highcharts are loaded as DLL dynamically
// 'require'd modules however are evaluated only when 'require' is call, so it allows us to evaluate highcharts modules only when some component actually renders Chart
// usually it means all DLL dependencies are ensured
let Highcharts;
function loadHighcharts() {
    if (typeof DLL_PATHS !== 'undefined' && !window.highcharts) {
        throw new Error('highcharts DLL has to be loaded before using Chart.');
    }

    // TODO use minified version + source-map-loader when highcharts support them
    // https://github.com/highcharts/highcharts/issues/1902
    // currently they don't provide #sourceMappingURL, so loader doesn't know about maps existence
    // also it seems maps are somehow corrupted - after manual addition of #sourceMappingURL, it could find the files and parse them as JSON,
    Highcharts = require('highcharts/highstock.src');
    const HCMorePlugin = require('highcharts/highcharts-more.src');
    const HCDataPlugin = require('highcharts/modules/data.src');
    const HCSolidGaugePlugin = require('highcharts/modules/solid-gauge.src');
    const HCFunnelPlugin = require('highcharts/modules/funnel.src');
    const HCPatternPlugin = require('highcharts/modules/pattern-fill.src');
    const HCDraggablePointsPlugin = require('highcharts/modules/draggable-points.src');
    const HCExportPlugin = require('highcharts/modules/exporting.src');
    const HCOfflineExportPlugin = require('highcharts/modules/offline-exporting.src');
    const HCSankeyPlugin = require('highcharts/modules/sankey.src');

    if (DEBUG) {
        // show some useful names in call stack and errors instead of a bunch of 'anonymous'
        Highcharts.wrap = function (obj, method, func, plugin) {
            var proceed = obj[method],
                pluginName = plugin && plugin.name,
                pluginPathPart = pluginName ? pluginName + '.' : '',
                realPath = 'obj',
                displayPath = pluginPathPart + 'wrappedObj';
            for (const fieldName in Highcharts) {
                if (Highcharts[fieldName] === obj) {
                    realPath = `Highcharts.${fieldName}`;
                    displayPath = pluginPathPart + fieldName;
                    break;
                }
                if (Highcharts[fieldName] && Highcharts[fieldName].prototype === obj) {
                    realPath = `Highcharts.${fieldName}.prototype`;
                    displayPath = pluginPathPart + fieldName;
                }
            }
            for (const fieldName in Highcharts.seriesTypes) {
                if (Highcharts.seriesTypes[fieldName] === obj) {
                    realPath = `Highcharts.seriesTypes.${fieldName}`;
                    displayPath = pluginPathPart + fieldName;
                    break;
                }
                if (Highcharts.seriesTypes[fieldName] && Highcharts.seriesTypes[fieldName].prototype === obj) {
                    realPath = `Highcharts.seriesTypes.${fieldName}.prototype`;
                    displayPath = pluginPathPart + fieldName;
                }
            }
            eval(`
                var ${pluginName ? pluginName + ' = {};' : ''} ${displayPath} = ${realPath};
                ${displayPath}.${method}.wrap = function() {
                    var args = Array.prototype.slice.call(arguments),
                        outerArgs = arguments,
                        ctx = this,
                        ret;
                    ctx.proceed = function () {
                        proceed.apply(ctx, arguments.length ? arguments : outerArgs);
                    };
                    args.unshift(proceed);
                    ret = func.apply(this, args);
                    ctx.proceed = null;
                    return ret;
                };

                ${displayPath}.${method} = ${displayPath}.${method}.wrap;
            `);
        };
    }

    const HCBugWorkaroundsPlugin = function (H) {
        // a hack implementing point-level linkedTo https://github.com/highcharts/highcharts/issues/2079
        H.wrap(H.seriesTypes.pie.prototype.pointClass.prototype, 'setVisible', function (proceed, value) {
            this.series.chart.series.forEach(serie => serie && serie.data.forEach(point => {
                if (point && point.options && !point.isNull && point.options.linkedTo !== undefined && this.id !== undefined && point.options.linkedTo === this.id) {
                    point.setVisible(value);
                }
            }));
            proceed.call(this, value);
        }, HCBugWorkaroundsPlugin);
        H.wrap(H.Point.prototype, 'setState', function (proceed, state) {
            this.series.chart.series.forEach(serie => serie && serie.data.forEach(point => {
                if (point && point.options && !point.isNull && point.options.linkedTo !== undefined && this.id !== undefined && point.options.linkedTo === this.id) {
                    point.setState(state);
                }
            }));

            if (this.series.type !== 'pie' || !!this.series.halo || this.series.data.some(point => point && point.graphic)) {
                proceed.call(this, state);
            }           
        }, HCBugWorkaroundsPlugin);


        // a hack to avoid https://github.com/highcharts/highcharts/issues/7373
        // each Point contains it's index in Serie.data array; for some reason they are updated only when redraw happens
        // but Chart.get (or Point.update?) uses this index to find the point to update
        // if of three point we remove two points it reduces array length to 1
        // and then if we try to update the third one, it will actually update Serie.data[2] creating a hole in the array
        // just force updating indices after remove seems to help
        H.wrap(H.Point.prototype, 'remove', function (proceed, redraw) {
            const series = this.series;
            proceed.call(this, redraw);
            series.data.forEach((point, index) => point.index = index);
        }, HCBugWorkaroundsPlugin);
    };

    const HCPercentageSizePlugin = function (H) {
        // adds support of percentage values to some properties
        // if related property is not set percentage property acts as if it was not set as well
        // plotOptions.pie.dataLabels.distance - relative to outer radius
        // series<pie>.dataLabels.distance - relative to outer radius
        // legend.width - relative to chart width
        // legend.maxHeight - relative to chart height
        // legend.itemWidth - relative to legend.width
        // legend.itemStyle.fontSize - relative to legend.maxHeight
        function calculateAbsoluteValuesAndProceed(proceed, ...args) {
            const originals = {},
                wrap = (options, prop, relatedOptions, relatedProp, suffix) => {
                    if (options && options[prop] && options[prop].includes && options[prop].includes('%') && typeof relatedOptions[relatedProp] === 'number') {
                        originals[prop] = options[prop];
                        options[prop] = relatedOptions[relatedProp] * parseInt(options[prop]) / 100;
                        if (suffix) {
                            options[prop] = options[prop] + suffix;
                        }
                    }
                },
                unwrap = (options, prop, relatedOptions, relatedProp, suffix) => {
                    if (originals.hasOwnProperty(prop)) {
                        options[prop] = originals[prop];
                    }
                }

            wrap(this.options, 'width', this.chart, 'chartWidth');
            wrap(this.options, 'maxHeight', this.chart, 'chartHeight');
            wrap(this.options, 'itemWidth', this.options, 'width');
            wrap(this.options.itemStyle, 'fontSize', this.options, 'maxHeight', 'px');

            proceed.call(this, ...args);

            unwrap(this.options, 'width', this.chart, 'chartWidth');
            unwrap(this.options, 'maxHeight', this.chart, 'chartHeight');
            unwrap(this.options, 'itemWidth', this.options, 'width');
            unwrap(this.options.itemStyle, 'fontSize', this.options, 'maxHeight', 'px');
        }


        H.wrap(H.Legend.prototype, 'render', calculateAbsoluteValuesAndProceed, HCPercentageSizePlugin);

        H.wrap(H.Legend.prototype, 'renderItem', calculateAbsoluteValuesAndProceed, HCPercentageSizePlugin);

        H.wrap(H.seriesTypes.pie.prototype, 'translate', function (proceed, positions) {
            if (!positions) {
                this.center = positions = this.getCenter();
            }

            let originalDistance = null;
            if (this.options && this.options.dataLabels && this.options.dataLabels.distance && this.options.dataLabels.distance.includes && this.options.dataLabels.distance.includes('%')) {
                originalDistance = this.options.dataLabels.distance;
                this.options.dataLabels.distance = positions[2] / 2 * parseInt(this.options.dataLabels.distance) / 100;
            }

            proceed.call(this, positions);

            if (originalDistance !== null) {
                this.options.dataLabels.distance = originalDistance;
            }
        }, HCPercentageSizePlugin);
    };

    const HCScalePlugin = function (H) {
        // the same chart might not look good both in a small box and fullscreen: 
        // font sizes, margins etc are defined in pixels and they don't scale as the chart itself
        // making them either too small on a huge screen, or overflowing on a small one
        // this plugin adds couple of properties to scale the chart as a picture
        // chart.naturalWidth
        // chart.naturalHeight
        // both of them are pixel values
        // if only one of them is specified, the second one will be calculated to preserve container's aspect ratio
        H.wrap(H.Chart.prototype, 'getChartSize', function (proceed, ...args) {
            const originalWidth = this.options.chart.width,
                originalHeight = this.options.chart.height;
            if (this.options.chart.naturalWidth || this.options.chart.naturalHeight) {
                const computedStyle = !this.options.chart.width || !this.options.chart.height ? window.getComputedStyle(this.renderTo) : null;
                const realWidth = this.options.chart.width || this.renderTo.offsetWidth - (parseInt(computedStyle.paddingLeft) || 0) - (parseInt(computedStyle.paddingRight) || 0);
                const realHeight = this.options.chart.height || this.renderTo.offsetHeight - (parseInt(computedStyle.paddingTop) || 0) - (parseInt(computedStyle.paddingBottom) || 0);

                this.options.chart.width = this.options.chart.naturalWidth || Math.round(realWidth * this.options.chart.naturalHeight / realHeight);
                this.options.chart.height = this.options.chart.naturalHeight || Math.round(realHeight * this.options.chart.naturalWidth / realWidth);

                this.scaleX = realWidth / this.options.chart.width;
                this.scaleY = realHeight / this.options.chart.height;
            }
            else {
                this.scaleX = null;
                this.scaleY = null;
            }

            proceed.call(this, ...args);

            this.options.chart.width = originalWidth;
            this.options.chart.height = originalHeight;
        }, HCScalePlugin);

        H.wrap(H.Chart.prototype, 'render', function (proceed) {
            proceed.call(this);

            if (this.scaleX || this.scaleY) {
                this.container.style.transform =
                    this.scaleX && this.scaleY ? `translate(-50%, -50%) scale(${this.scaleX}, ${this.scaleY}) translate(50%, 50%)` :
                        this.scaleX ? `translate(-50%, -50%) scaleX(${this.scaleX}) translate(50%, 50%)` :
                            this.scaleY ? `translate(-50%, -50%) scaleY(${this.scaleY}) translate(50%, 50%)` :
                                '';
            }
        }, HCScalePlugin);

        H.wrap(H.Chart.prototype, 'setSize', function (proceed, width, height, animation) {
            proceed.call(this, width, height, animation);

            if (this.scaleX || this.scaleY) {
                this.container.style.transform =
                    this.scaleX && this.scaleY ? `translate(-50%, -50%) scale(${this.scaleX}, ${this.scaleY}) translate(50%, 50%)` :
                        this.scaleX ? `translate(-50%, -50%) scaleX(${this.scaleX}) translate(50%, 50%)` :
                            this.scaleY ? `translate(-50%, -50%) scaleY(${this.scaleY}) translate(50%, 50%)` :
                                '';
            }
        }, HCScalePlugin);

        H.wrap(H.Tooltip.prototype, 'refresh', function (proceed, ...args) {
            proceed.call(this, ...args);

            if (this.scaleX || this.scaleY) {
                let transform = this.label.element.getAttribute('transform');
                if (!transform.includes('scale')) {
                    this.label.element.setAttribute('transform',
                        (
                            this.chart.scaleX && this.chart.scaleY ? 'scale(' + 1 / this.chart.scaleX + ',' + 1 / this.chart.scaleY + ') ' :
                                this.chart.scaleX ? 'scaleX(' + 1 / this.chart.scaleX + ') ' :
                                    this.chart.scaleY ? 'scaleY(' + 1 / this.chart.scaleY + ') ' :
                                        ''
                        ) + transform
                    );
                }
            }
        }, HCScalePlugin);

        H.wrap(H.Tooltip.prototype, 'move', function (proceed, ...args) {
            proceed.call(this, ...args);

            let transform = this.label.element.getAttribute('transform');
            if (!transform.includes('scale')) {
                this.label.element.setAttribute('transform',
                    (
                        this.chart.scaleX && this.chart.scaleY ? 'scale(' + 1 / this.chart.scaleX + ',' + 1 / this.chart.scaleY + ') ' :
                            this.chart.scaleX ? 'scaleX(' + 1 / this.chart.scaleX + ') ' :
                                this.chart.scaleY ? 'scaleY(' + 1 / this.chart.scaleY + ') ' :
                                    ''
                    ) + transform
                );
            }
        }, HCScalePlugin);
    };

    const HCHTMLLegendPlugin = function (H) {
        // adds some styles to HTML legend items to make them work more like a big HTML block rather than a set of randomly positioned nodes

        function resetStyles(legend, item) {
            if (legend.options.useHTML && legend.options.layout === 'vertical' && item.legendItem) {
                item.legendItem.element.style.display = '';
                item.legendItem.element.style.width = ``;
            }
        }

        H.wrap(H.Legend.prototype, 'setText', function (proceed, item) {
            resetStyles(this, item);

            proceed.call(this, item);

            // if there's itemWidth property, we should set it to the item so item styles could utilize floats, positions, relative sizes etc
            // there's no dedicated method to do this, but setText's "after" happens when the whole item has been constructed and legend starts to fetch sizes data
            if (this.options.useHTML) {
                if (this.options.itemWidth) {
                    item.legendItem.element.style.display = 'inline-block';
                    item.legendItem.element.style.width = `${this.options.itemWidth - this.symbolWidth - this.options.symbolPadding}px`;
                    item.legendItem.getBBox(true);
                }
            }
        }, HCHTMLLegendPlugin);

        H.wrap(H.Legend.prototype, 'render', function (proceed) {
            if (this.allItems) {
                this.allItems.forEach(item => resetStyles(this, item));
            }

            proceed.call(this);

            if (this.options.useHTML) {
                // if there's no itemWidth, let vertically layouted items act as block elements inside inline-block legend
                if (this.options.layout === 'vertical' && !this.options.itemWidth) {
                    this.allItems.forEach(item => {
                        item.legendItem.element.style.display = 'inline-block';
                        item.legendItem.element.style.width = `${this.maxItemWidth - this.symbolWidth - this.options.symbolPadding}px`;
                    });
                }
            }
        }, HCHTMLLegendPlugin);

        H.wrap(H.Legend.prototype, 'positionItem', function (proceed, item) {
            proceed.call(this, item);

            if (this.options.useHTML && item.legendItem.element.offsetHeight) {
                // add legend.symbolVerticalAlign option for arbitrary sizes of item markup
                if (this.options.symbolVerticalAlign && item.legendSymbol) {
                    if (this.options.symbolVerticalAlign === 'top') {

                    }
                    if (this.options.symbolVerticalAlign === 'middle') {
                        item.legendSymbol.translate(0, (item.legendItem.element.offsetHeight - item.legendSymbol.element.getBBox().height) / 2 - 1);
                    }
                    if (this.options.symbolVerticalAlign === 'bottom') {
                        item.legendSymbol.translate(0, (item.legendItem.element.offsetHeight - item.legendSymbol.element.getBBox().height) - 1);
                    }
                }
            }
        }, HCHTMLLegendPlugin);
    };

    const HCLegendSeparatorPlugin = function (H) {
        // adds support of separators between legend items
        // if related property is not set percentage property acts as if it was not set as well
        // legend.separator.enabled = false
        // legend.separator.width = common.defaultBorderWidth
        // legend.separator.color = common.defaultBorderColor

        H.wrap(H.Legend.prototype, 'renderItem', function (proceed, item) {
            proceed.call(this, item);

            if (this.options.separator && this.options.separator.enabled && this.allItems[this.allItems.length - 1] !== item) {
                (
                    item.legendSeparator = item.legendSeparator || this.chart.renderer
                        .rect()
                        .addClass('highcharts-legend-separator')
                        .add(item.legendGroup)
                )
                    .attr({
                        x: 0,
                        y: (this.options.itemMarginTop || 0) + this.lastLineHeight + (this.options.itemMarginBottom || 0) + 2 /* SVG parts are not perfectly aligned with HTML ones, magic number to compensate misalignment */,
                        width: this.options.itemWidth || 0,
                        height: this.options.separator.width || common.borderWidthDefault,
                        fill: this.options.separator.color || common.borderColorDefault
                    });
            } else if (item.legendSeparator) {
                item.legendSeparator.destroy();
                item.legendSeparator = null;
            }
        }, HCLegendSeparatorPlugin);
    };

    const HCDataLabelsStatePlugin = function (H) {
        // adds state-level 'dataLabels' option

        H.wrap(H.Series.prototype, 'drawDataLabels', function (proceed) {
            const oldGroup = this.dataLabelsGroup;

            const originalDataLabels = this.options.dataLabels;
            if (this.state && this.options.states && this.options.states[this.state] && this.options.states[this.state].dataLabels) {
                this.options.dataLabels = {
                    ...this.options.dataLabels,
                    ...this.options.states[this.state].dataLabels
                };
            } else if (this.options.dataLabels === undefined) {
                this.options.dataLabels = { enabled: false };
            }

            proceed.call(this);

            if ((!this.options.dataLabels || !this.options.dataLabels.enabled) && this.dataLabelsGroup) {
                this.dataLabelsGroup.destroy();
                this.dataLabelsGroup = null;

                this.data.forEach(point => {
                    if (point.dataLabel) {
                        point.dataLabel.destroy();
                        point.dataLabel = null;
                    }
                });
            }

            this.options.dataLabels = originalDataLabels || { enabled: false };

            const newGroup = this.dataLabelsGroup;

            if (!oldGroup && newGroup) {
                this.points.forEach(point => {
                    if (point.dataLabel) {
                        (point.dataLabel.div || point.dataLabel.element).point = point;
                    }
                });

                this.dataLabelsGroup
                    .addClass('highcharts-tracker')
                    .on('mouseover', event => {
                        const point = this.chart.pointer.getPointFromEvent(event);
                        if (this.chart.pointer.isDirectTouch = !!point) {
                            point.onMouseOver(event);
                        }
                    })
                    .on('mouseout', event => {
                        this.chart.pointer.onTrackerMouseOut(event);
                    });


                if (this.options.cursor) {
                    this.dataLabelsGroup
                        .css(H.css)
                        .css({ cursor: this.options.cursor });
                }
            }
        }, HCDataLabelsStatePlugin);

        H.wrap(H.Series.prototype, 'setState', function (proceed, state) {
            const redrawNeeded = !this.baseSeries && this.options.states && (
                this.options.states[this.state] && this.options.states[this.state].dataLabels ||
                this.options.states[state] && this.options.states[state].dataLabels
            );

            proceed.call(this, state);

            if (redrawNeeded) {
                this.drawDataLabels();
            }
        }, HCDataLabelsStatePlugin);
    };

    const HCSerieOpacityPlugin = function (H) {
        // adds serie-level 'opacity' and 'states.<state>.opacity' options
        H.wrap(H.Series.prototype, 'render', function (proceed, ...args) {
            proceed.call(this, ...args);

            const opacity = this.options.states && this.options.states[this.state] && this.options.states[this.state].opacity || this.options.opacity || 1;

            if (this._opacity !== opacity) {
                this._opacity = opacity;

                [this.group, this.markerGroup, this.dataLabelsGroup].filter(v => v).forEach(group => {
                    group.attr({ opacity });
                });
            }
        }, HCSerieOpacityPlugin);

        H.wrap(H.Series.prototype, 'setState', function (proceed, state) {
            proceed.call(this, state);

            const opacity = this.options.states && this.options.states[state] && this.options.states[state].opacity || this.options.opacity || 1;

            if (this._opacity !== opacity) {
                this._opacity = opacity;

                [this.group, this.markerGroup, this.dataLabelsGroup].filter(v => v).forEach(group => {
                    group.animate(
                        { opacity },
                        this.chart.options.chart.animation ||
                        this.options.states && this.options.states[state] && this.options.states[state].animation
                    );
                });
            }
        }, HCSerieOpacityPlugin);
    };

    const HCUnhoverStatePlugin = function (H) {
        // adds 'unhover' state which is applied to every serie when some other serie is hovered
        // TODO also point-level one

        for (const type in H.defaultOptions.plotOptions) {
            const states = H.defaultOptions.plotOptions[type].states;

            states.unhover = { animation: states.hover.animation };

            if (H.defaultOptions.plotOptions[type].marker) {
                H.defaultOptions.plotOptions[type].marker.states.unhover = {};
            }
        }

        H.wrap(H.Series.prototype, 'setState', function (proceed, state) {
            const currentSerie = this;

            if (!state && this.chart.series.some(serie => serie !== currentSerie && !serie.baseSeries && serie.state === 'hover')) {
                state = 'unhover';
            }

            proceed.call(this, state);

            if (
                this.chart.series.some(serie => !serie.baseSeries && serie.state === 'hover') &&
                !this.chart.series.some(serie => !serie.baseSeries && serie.state === 'unhover')
            ) {
                this.chart.series.forEach(serie => {
                    if (serie !== currentSerie && !serie.baseSeries && serie.state === '') {
                        serie.setState('unhover', true);
                    }
                });
            }
            else if (
                !this.chart.series.some(serie => !serie.baseSeries && serie.state === 'hover') &&
                this.chart.series.some(serie => !serie.baseSeries && serie.state === 'unhover')
            ) {
                this.chart.series.forEach(serie => {
                    if (serie !== currentSerie && !serie.baseSeries && serie.state === 'unhover') {
                        serie.setState('', true);
                    }
                });
            }
        }, HCUnhoverStatePlugin);
    };

    const HCForcedStatePlugin = function (H) {
        // adds 'state' option to series which forces the chart to always show serie with such state regardless of mouse events 
        // TODO also point-level one

        H.wrap(H.Series.prototype, 'update', function (proceed, newOptions, ...rest) {
            if (this.options.state && !newOptions.state) {
                const originalState = this.options.state;
                this.options.state = '';
                this.setState('');
                this.options.state = originalState;
            }

            proceed.call(this, newOptions, ...rest);
        }, HCForcedStatePlugin);

        H.wrap(H.Series.prototype, 'setState', function (proceed, state) {
            proceed.call(this, this.options.state || state);
        }, HCForcedStatePlugin);

        H.wrap(H.Series.prototype, 'render', function (proceed, ...args) {
            proceed.call(this, ...args);

            if (this.options.hasOwnProperty('state')) {
                this.setState(this.options.state);
            }
        }, HCForcedStatePlugin);
    };

    const HCAutoPaneSizePlugin = function (H) {
        // allows to pass "auto" as pane.size making the pane take all possible space without being cut off

        function between(angle, startAngle, endAngle) {
            while (endAngle <= startAngle) {
                endAngle += 360;
            }
            while (angle > startAngle) {
                angle -= 360;
            }
            while (angle < startAngle) {
                angle += 360;
            }

            return startAngle <= angle && angle <= endAngle;
        }

        H.wrap(H.Pane.prototype, 'updateCenter', function (proceed, axis) {
            const isAuto = this.options.size === 'auto';

            if (isAuto) {
                this.options.size = '100%';
            }

            proceed.call(this, axis);

            if (isAuto) {
                const
                    [cx, cy, outerSize, innerSize] = this.center,
                    slicingRoom = 2 * (this.options.slicedOffset || 0),
                    plotWidth = this.chart.plotWidth - 2 * slicingRoom,
                    plotHeight = this.chart.plotHeight - 2 * slicingRoom,
                    availableLeft = Math.max(0, cx),
                    availableRight = Math.min(plotWidth - cx, plotWidth),
                    availableTop = Math.max(0, cy),
                    availableBottom = Math.min(plotHeight - cy, plotHeight),
                    startAngle = this.options.startAngle || 0,
                    endAngle = this.options.endAngle !== undefined ? this.options.endAngle : startAngle + 360;

                if (startAngle % 90 || endAngle % 90) {
                    throw new Error('Not implemented. Currently HCAutoPaneSizePlugin works only with startAngle/endAngle divisible by 90 degrees.')
                }

                const
                    neededTop = between(0, startAngle, endAngle) ? 1 : 0,
                    neededRight = between(90, startAngle, endAngle) ? 1 : 0,
                    neededBottom = between(180, startAngle, endAngle) ? 1 : 0,
                    neededLeft = between(270, startAngle, endAngle) ? 1 : 0,
                    smallestRadius = Math.min(
                        availableLeft ? availableLeft / neededLeft : Infinity,
                        availableRight ? availableRight / neededRight : Infinity,
                        availableTop ? availableTop / neededTop : Infinity,
                        availableBottom ? availableBottom / neededBottom : Infinity
                    ),
                    diameter = smallestRadius === Infinity ? outerSize : smallestRadius * 2;

                this.center[2] = diameter * this.center[2] / this.center[2];
                this.center[3] = diameter * this.center[3] / this.center[2];

                this.options.size = 'auto';
            }

        }, HCAutoPaneSizePlugin);
    }

    function HCDateFormatsPlugin(H) {
        const year = H.timeUnits.year;
        delete H.timeUnits.year; // Highcharts rely on ascending order of `foreach (n in H.timeUnits)`, which usually is defined by the order of how properties have been added, so first we have to remove all units higher than quarter
        H.timeUnits.quarter = 3 * H.timeUnits.month;
        H.timeUnits.year = year;

        H.dateFormats = {
            TD: function (timestamp) {
                return TimeStamp.toString(this.options.intl, new Date(timestamp), null, PeriodType.DAY, TimeStamp.Format.SHORT, null, false, this.options.fiscalPeriodStartMonth);
            },
            TW: function (timestamp) {
                return TimeStamp.toString(this.options.intl, new Date(timestamp), null, PeriodType.WEEK, TimeStamp.Format.SHORT, null, false, this.options.fiscalPeriodStartMonth);
            },
            TM: function (timestamp) {
                return TimeStamp.toString(this.options.intl, new Date(timestamp), null, PeriodType.MONTH, TimeStamp.Format.SHORT, null, false, this.options.fiscalPeriodStartMonth);
            },
            TQ: function (timestamp) {
                return TimeStamp.toString(this.options.intl, new Date(timestamp), null, PeriodType.QUARTER, TimeStamp.Format.SHORT, null, false, this.options.fiscalPeriodStartMonth);
            },
            TY: function (timestamp) {
                return TimeStamp.toString(this.options.intl, new Date(timestamp), null, PeriodType.YEAR, TimeStamp.Format.SHORT, null, false, this.options.fiscalPeriodStartMonth);
            }
        }

        // also there should be extension of default options for axis.units and axis.dateTimeLabelFormats, and default value for intl, but
    }

    const HCAxisPaddingPlugin = function (H) {
        // enables axes' min- and maxPadding behavior in case of start- or endOnTick=true 
        // it works pretty much the same, but relative to min/max tick rather than min/max point

        H.wrap(H.Axis.prototype, 'trimTicks', function (proceed, tickPositions, startOnTick, endOnTick) {
            proceed.call(this, tickPositions, startOnTick, endOnTick);

            if (!this.isLinked) {
                const length = this.max - this.min;

                if (startOnTick) {
                    this.min -= (this.options.minPadding || 0) * length;
                }

                if (endOnTick) {
                    this.max += (this.options.maxPadding || 0) * length;
                }
            }
        }, HCAxisPaddingPlugin);
    };

    HCMorePlugin(Highcharts);
    HCDataPlugin(Highcharts);
    HCSolidGaugePlugin(Highcharts);
    HCFunnelPlugin(Highcharts);
    HCPatternPlugin(Highcharts);
    HCDraggablePointsPlugin(Highcharts);
    HCBugWorkaroundsPlugin(Highcharts);
    HCPercentageSizePlugin(Highcharts);
    HCScalePlugin(Highcharts);
    HCHTMLLegendPlugin(Highcharts);
    HCLegendSeparatorPlugin(Highcharts);
    HCDataLabelsStatePlugin(Highcharts);
    HCSerieOpacityPlugin(Highcharts);
    HCUnhoverStatePlugin(Highcharts);
    HCForcedStatePlugin(Highcharts);
    HCAutoPaneSizePlugin(Highcharts);
    HCDateFormatsPlugin(Highcharts);
    HCAxisPaddingPlugin(Highcharts);
    HCExportPlugin(Highcharts);
    HCOfflineExportPlugin(Highcharts);
    HCSankeyPlugin(Highcharts);

    // Custom lowmedhigh serie type based on https://www.highcharts.com/demo/advanced-accessible
    Highcharts.seriesType('lowmedhigh', 'boxplot', {
        keys: ['low', 'median', 'high'],
        tooltip: {
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: ' +
                'Low <b>{point.low}</b> - Median <b>{point.median}</b> - High <b>{point.high}</b><br/>'
        }
    }, {
            drawPoints() {
                const series = this;
                this.points.forEach((point) => {
                    let { graphic } = point;
                    const
                        verb = graphic ? 'animate' : 'attr',
                        { shapeArgs } = point,

                        width = shapeArgs.width,                                       
                        left = Math.floor(shapeArgs.x) + 0.5,
                        right = left + width,
                        crispX = left + Math.round(width / 2) + 0.5,
                        
                        highPlot = Math.floor(point.highPlot) + 0.5,
                        medianPlot = Math.floor(point.medianPlot) + 0.5,
                        lowPlot = Math.floor(point.lowPlot) + 0.5 - (point.low === 0 ? 1 : 0);

                    if (point.isNull) { return; }

                    if (!graphic) {
                        point.graphic = graphic = series.chart.renderer
                            .path('point')
                            .add(series.group);
                    }

                    graphic.attr({
                        stroke: point.color || series.color,
                        'stroke-width': 1
                    });

                    graphic[verb]({
                        d: [
                            'M', left, highPlot,
                            'H', right,
                            'M', left, medianPlot,
                            'H', right,
                            'M', left, lowPlot,
                            'H', right,
                            'M', crispX, highPlot,
                            'V', lowPlot
                        ]
                    });
                });
            }
        });

    return Highcharts;
};

export const PALETTE = [
    'rgb(124, 181, 236)',
    'rgb(67, 67, 72)',
    'rgb(144, 237, 125)',
    'rgb(247, 163, 92)',
    'rgb(128, 133, 233)',
    'rgb(241, 92, 128)',
    'rgb(228, 211, 84)',
    'rgb(43, 144, 143)',
    'rgb(244, 91, 91)',
    'rgb(145, 232, 225)'
];

export const ChartPreset = Enum('SPARKLINE', 'CHART');

const DATE_TIME_LABEL_FORMATS = {
    millisecond: '%H:%M:%S.%L',
    second: '%H:%M:%S',
    minute: '%H:%M',
    hour: '%H:%M',
    day: `%TD`,
    week: `%TW`,
    month: `%TM`,
    quarter: `%TQ`,
    year: `%TY`
};

const UNITS = [[
    'millisecond', // unit name
    [1, 2, 5, 10, 20, 25, 50, 100, 200, 500] // allowed multiples
], [
    'second',
    [1, 2, 5, 10, 15, 30]
], [
    'minute',
    [1, 2, 5, 10, 15, 30]
], [
    'hour',
    [1, 2, 3, 4, 6, 8, 12]
], [
    'day',
    [1]
], [
    'week',
    [1]
], [
    'month',
    [1, 3, 6]
], /*[
    'quarter', // TODO provide "natural" unit; Highcharts select unit only by the distance between closest ticks - the unit is the biggest one that can still differentiate them, and since ticks can be omitted to fit the screen (like show every 6'th month), chart switches to quarters even with monthly data
    [1]
],*/ [
    'year',
    null
]];

const PRESET_OPTIONS_MAP = {
    [ChartPreset.SPARKLINE]: {
        credits: {
            enabled: false
        },
        chart: {
            backgroundColor: 'rgba(0, 0, 0, 0)',
            spacingTop: 0,
            spacingBottom: 0,
            spacingLeft: 0,
            spacingRight: 0,
            margin: 0,
            width: null,
            height: null,
            style: { ...DEFAULT_FONT_STYLE }
        },
        plotOptions: {
            series: {
                enableMouseTracking: false,
                marker: { enabled: false },
                dataLabels: { borderWidth: 0 }
            }
        },
        tooltip: {
            enabled: false,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS
        },
        title: { text: null },
        subTitle: { text: null },
        xAxis: [{
            id: 'xAxis',
            minPadding: 0,
            maxPadding: 0,
            startOnTick: false,
            endOnTick: false,
            title: { text: null },
            visible: false,
            units: UNITS,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS

        }],
        yAxis: [{
            id: 'yAxis',
            minPadding: 0,
            maxPadding: 0,
            startOnTick: false,
            endOnTick: false,
            title: { text: null },
            visible: false,
            units: UNITS,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS
        }],
        legend: { enabled: false },
        navigator: { enabled: false },
        exporting: { buttons: { contextButton: { enabled: false } } }
    },
    [ChartPreset.CHART]: {
        credits: {
            enabled: false
        },
        chart: {
            spacingBottom: 0,
            spacingLeft: 0,
            spacingRight: 0,
            width: null,
            height: null,
            style: { ...DEFAULT_FONT_STYLE }
        },
        tooltip: {
            valueDecimals: 2,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS
        },
        title: { text: null },
        subTitle: { text: null },
        xAxis: [{
            id: 'xAxis',
            title: {
                text: null
            },
            units: UNITS,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS
        }],
        yAxis: [{
            id: 'yAxis',
            title: {
                text: null
            },
            units: UNITS,
            dateTimeLabelFormats: DATE_TIME_LABEL_FORMATS
        }],
        legend: { alignColumns: false },
        navigator: { enabled: true },
        exporting: { buttons: { contextButton: { enabled: false } } }
    }
};

const DEFAULT_COMPONENT_DATA = {
    getter: null,
    component: null,
    path: '',
    commonPath: '',
    useHTML: false,
    container: null,
    observer: null,
    output: null,
    rendered: false,
    hashExtractor: (context, ...args) => {
        const idable = context.hasOwnProperty('id') ? context : context.point || context.series || context;
        return idable.hasOwnProperty('id') ? idable.id :
            idable.hasOwnProperty('index') ? idable.index : null;
    }
};

export const DEFAULT_FONT_STYLE = {
    fontSize: common.fontSizeNormal,
    fontWeight: common.fontWeightNormal,
    fontFamily: common.fontFamilyNormal,
    color: common.fontColorDefault,
    textOutline: 'none'
};

const LOGGING_ENABLED = DEBUG && false;
const log = DEBUG && LOGGING_ENABLED ?
    (chart, path, message, param) => {
        if (typeof param === 'string') {
            if (param.includes('<')) {
                const wrapper = document.createElement('div');
                wrapper.innerHTML = param;
                param = wrapper.firstElementChild;
            } else {
                param = JSON.stringify(param);
            }
        }

        console.log(`Chart#${chart._id}${path || ''} ${message}`, param || '');
    } : null;

class PassContext extends React.Component {
    static get propTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    static get childContextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    getChildContext() {
        return { intl: this.props.intl, router: this.props.router, store: this.props.store };
    }

    render() {
        return this.props.children;
    }
}

export default class Chart extends React.PureComponent {
    static get Highcharts() {
        return Highcharts = Highcharts || loadHighcharts();
    }

    static get ChartPreset() {
        return ChartPreset;
    }

    static get PALETTE() {
        return PALETTE;
    }

    static get DEFAULT_FONT_STYLE() {
        return DEFAULT_FONT_STYLE;
    }

    static get propTypes() {
        return {
            // some commonly used default settings, will be extended with options
            preset: PropTypes.oneOf(Object.values(ChartPreset)).isRequired,
            // highcharts options object http://api.highcharts.com/highcharts/Chart 
            // with partial React components support in place of any string property:
            //      - if property has corresponding useHTML=true, component will be rendered and property will be replaced with its innerHTML
            //      - if property has no useHTML, innerText will be used
            //      - since there is no way to pass DOM to HC, no user events will be handled
            //      - internal components updates (timeouts, HTTP requests, store changes) will be handled and HC will be updated (not in IE < 11)
            //      - functions returning strings(formatters) can also return React components, but since one function can return strings for multiple items (points, series, axis labels etc)
            //        there should be some kind of persistent ID for each of such items for proper updates tracking
            //        by default Chart will try to extract some 'id' field from functions arguments
            //        but it's also possible to provide custom '<formatterPropertyName>Hash' function - 
            //        it receives the same arguments as formatter function and returns string or numeric hashExtractor of arguments
            // if some property in options is a function, "this" will be passed as first parameter instead of context
            // to allow components pass their bound event handlers instead of lambdas and reduce number of useless updates (it's hard to check if two lambdas do exactly the same stuff so any lambda always causes force update)
            options: PropTypes.object.isRequired,
            className: PropTypes.string
        }
    }

    static get defaultProps() {
        return {
            preset: ChartPreset.CHART,
            className: ''
        }
    }

    static get contextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    _id = 'chart' + ((Math.random() * Number.MAX_SAFE_INTEGER) | 0);

    _chart = null;

    _isNestedComponentUpdate = false;

    _reactComponentUpdateBatchTimeoutID = null;

    _reactComponentUpdateBatchStates = [];

    _components = {};

    state = { options: null };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentDidMount() {
        if (this.state.options) {
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Creating new chart: ', this.state.options); }
            if (!this._chart && this.state.options) {
                try {
                    this._chart = new Chart.Highcharts.Chart(this._id, deepClone(this.state.options));
                }
                catch (e) {
                    console.error(e);
                }
            }
            if (this._chart) {
                window.addEventListener('resize', this._onWindowResize);
                this._onWindowResize();
            }
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Chart created.'); }
        }
    }

    _generateOnDemandRenderer(path) {
        const self = this;
        return function (...args) {
            // Bug fix https://sentry.io/4straction-ltd/4straction/issues/361370288/
            // If some serieses were removed from the chart and the chart should be redrawn, 
            // highcharts tries to redraw series and labels that do not exist anymore in this._components and this causes an error.
            return self._components[path] ? self._prerenderReactComponentOnDemand(path, this, args) : null;
        }
    }

    _prerenderReactComponent(component, componentData, useObserver) {
        componentData = {
            ...componentData,
            component: componentData.component || null,
            observer: componentData.observer || null,
            container: componentData.container || document.createElement('div')
        };

        componentData.container.id = this._id + componentData.path.replace(/\./g, '__');

        componentData.observer = componentData.observer || new MutationObserver(this._onReactComponentUpdate.bind(this, componentData.path));

        componentData.observer.disconnect();

        if (useObserver) {
            componentData.observer.observe(componentData.container, { attributes: true, childList: true, characterData: true, subtree: true });
        }

        if (DEBUG && LOGGING_ENABLED) {
            log(this, componentData.path, !componentData.component ? 'Mounting new component.' : 'Updating component.');
        }

        // TODO rewrite this to properly handle asynchronous case of ReactDOM.render
        // currently it should work, but will cause unnecessary chart update
        let isSync = true;
        componentData.component = ReactDOM.render(<PassContext {...this.context}>{component}</PassContext>, componentData.container, () => {
            if (!isSync) {
                this._onReactComponentUpdate(componentData.path);
            }

            if (!useObserver) {
                componentData.observer.observe(componentData.container, { attributes: true, childList: true, characterData: true, subtree: true });
            }
        });
        isSync = false;

        // TODO always preserve HTML subset supported by highcharts: https://www.highcharts.com/docs/chart-concepts/labels-and-string-formatting#html
        // TODO try to enable full React functionality by using ReactDOMServer.renderToString and connecting to DOM after highcharts' render: https://facebook.github.io/react/docs/react-dom-server.html#rendertostring
        componentData.output = componentData.useLessShittyHTML ? componentData.container.outerHTML : componentData.useHTML ? componentData.container.innerHTML : componentData.container.innerText;

        if (DEBUG && LOGGING_ENABLED) {
            log(this, componentData.path, 'Component rendered: ', componentData.output);
        }

        return componentData;
    }

    _prerenderReactComponentOnDemand(commonPath, context, args) {
        const
            id = this._components[commonPath].hashExtractor(context, ...args),
            path = typeof id === 'string' ? commonPath + '.' + id :
                typeof id === 'number' ? commonPath + '[' + id + ']' :
                    commonPath,
            genericComponentData = this._components[commonPath],
            componentData = this._components[path] || {
                ...genericComponentData,
                path: path,
                commonPath: commonPath,
                component: null,
                observer: null,
                container: null,
                output: null
            };

        if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Invoking formatter.'); }


        if (this._isNestedComponentUpdate && componentData.output) {
            if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Returning from cache: ', componentData.output); }
            this._components[path].rendered = this._components[commonPath].rendered = true;
            // chart update happened because of some nested component update, this component already has rendered with actual values and we can return previous value
            return componentData.output;
        }

        const value = componentData.getter.call(null, context, ...args);
        if (React.isValidElement(value)) {
            if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Formatter returned React component, rendering.'); }
            // use observer to schedule async update to check if there's something to unmount
            this._components[path] = this._prerenderReactComponent(value, componentData, !this._isNestedComponentUpdate && !this._updateScheduled);
            this._components[path].rendered = this._components[commonPath].rendered = true;
            if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Returning: ', this._components[path].output); }
            return this._components[path].output;
        }
        else {
            if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Formatter returned string, returning: ', value); }
            return (this._components[path] = { ...componentData, output: value }).output;
        }
    }

    _replaceReactComponentsData(value, path, hashExtractor, useHTML, useLessShittyHTML, oldComponents, newComponents) {
        if (path === '.time') {
            return value;
        }

        if (React.isValidElement(value)) {
            newComponents[path] = this._prerenderReactComponent(value, {
                ...(oldComponents[path] || DEFAULT_COMPONENT_DATA),
                path: path,
                useHTML: useHTML,
                useLessShittyHTML
            });

            return newComponents[path].output;
        } else if (typeof value === 'function') {
            newComponents[path] = {
                ...(oldComponents[path] || DEFAULT_COMPONENT_DATA),
                getter: value,
                path: path,
                useHTML: useHTML,
                useLessShittyHTML,
                hashExtractor: hashExtractor || DEFAULT_COMPONENT_DATA.hashExtractor
            };

            if (oldComponents[path] && oldComponents[path].getter === newComponents[path].getter) {
                return oldComponents[path].renderer;
            }
            else {
                return newComponents[path].renderer = this._generateOnDemandRenderer(path);
            }
        }
        else if (Array.isArray(value)) {
            return value.map((element, index) => this._replaceReactComponentsData(element, path + '[' + index + ']', null, useHTML, useLessShittyHTML, oldComponents, newComponents))
        }
        else if (typeof value === 'object' && value) {
            const result = {};
            for (let i in value) {
                if (value.hasOwnProperty(i)) {
                    result[i] = this._replaceReactComponentsData(value[i], path + '.' + i, value[i + 'Hash'], value.useHTML || useHTML, value.useLessShittyHTML || useLessShittyHTML, oldComponents, newComponents);
                }
            }
            return result;
        }
        else {
            return value;
        }
    }

    _onReactComponentUpdate = (path) => {
        if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Component changed, updating cache.'); }

        const componentData = this._components[path],
            output = componentData.useLessShittyHTML ? componentData.container.outerHTML : componentData.useHTML ? componentData.container.innerHTML : componentData.container.innerText;
        let renderer = componentData.renderer;
        const nextState = {
            options: assignByPath(this.state.options,
                componentData.commonPath || componentData.path,
                componentData.getter ? (renderer = this._generateOnDemandRenderer(componentData.commonPath || componentData.path)) : output)
        };

        this._components[componentData.path] = { ...this._components[componentData.path], output: output, renderer: renderer };

        if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Cache updated: ', output); }

        if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Scheduling batch options update: ', nextState.options); }

        this._reactComponentUpdateBatchTimeoutID = this._reactComponentUpdateBatchTimeoutID || setTimeout(this._onReactComponentUpdateBatchTimeout, 0);
        this._reactComponentUpdateBatchStates.push(nextState);

        if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Scheduled.'); }
    }

    _onReactComponentUpdateBatchTimeout = () => {
        const nextState = this._reactComponentUpdateBatchStates.reduce((r, v) => deepAssign(r, v), this.state);

        if (DEBUG && LOGGING_ENABLED) { log(this, 'Batch updating options: ', this._reactComponentUpdateBatchStates, nextState.options); }

        this.setState(nextState);

        if (DEBUG && LOGGING_ENABLED) { log(this, 'Options updated.'); }

        this._reactComponentUpdateBatchTimeoutID = null;
        this._reactComponentUpdateBatchStates = [];
    }

    componentWillReceiveProps(nextProps) {
        const oldComponents = this._components,
            newComponents = {},
            presetOptions = PRESET_OPTIONS_MAP[nextProps.preset],
            valuePadding = 0.01,
            usedIDs = {};
        let minY = Infinity, maxY = -Infinity;
        const hasData = nextProps.options && (nextProps.options.data || nextProps.options.series && nextProps.options.series.length),
            series = hasData ?
                nextProps.options.series.map((serie, index) => {
                    const id = serie.hasOwnProperty('id') ? serie.id : ('serie_' + index);
                    if (usedIDs[id]) {
                        throw new Error(`Duplicate ID: ${id}. IDs must be unique across all objects in options.`);
                    }
                    usedIDs[id] = true;

                    if (typeof serie.yAxis === 'number') {
                        throw new Error('Referencing axes by index in not yet supported.');
                    }

                    return {
                        color: PALETTE[index % PALETTE.length],
                        ...serie,
                        id: serie.hasOwnProperty('id') ? serie.id : ('serie_' + index),
                        data: serie.data && this._isDetailedPoints(serie.data) ?
                            serie.data.map((point, pointIndex) => {
                                minY = Math.min(minY, point.y);
                                maxY = Math.max(maxY, point.y);
                                const id = point.hasOwnProperty('id') ? point.id : ('point_' + index + '_' + pointIndex);
                                if (usedIDs[id]) {
                                    throw new Error('Duplicate ID: ' + id + '. IDs must be unique across all objects in options.');
                                }
                                usedIDs[id] = true;
                                return { ...point, id }
                            }) :
                            serie.data.map((point, pointIndex) => {
                                minY = Math.min(minY, Array.isArray(point) ? point[1] : point);
                                maxY = Math.max(maxY, Array.isArray(point) ? point[1] : point);
                                return point;
                            })
                    };
                }) :
                null;

        let nextOptions = null;

        if (hasData) {
            nextOptions = deepAssign(presetOptions, {
                ...nextProps.options,
                time: deepAssign({ intl: this.context.intl }, nextProps.options.time || {}),
                series,
                xAxis: nextProps.options.xAxis && toArray(nextProps.options.xAxis).map((xAxis, index) => {
                    const id = xAxis.hasOwnProperty('id') ? xAxis.id : ('xAxis_' + index);
                    if (usedIDs[id]) {
                        throw new Error(`Duplicate ID: ${id}. IDs must be unique across all objects in options.`);
                    }
                    usedIDs[id] = true;
                    return deepAssign(xAxis, { ...presetOptions.xAxis[0], ...xAxis, id });
                }) || presetOptions.xAxis,
                yAxis: nextProps.options.yAxis && toArray(nextProps.options.yAxis || []).map((yAxis, index, { length: yAxesCount }) => {
                    const id = yAxis.hasOwnProperty('id') ? yAxis.id : ('yAxis_' + index);
                    if (usedIDs[id]) {
                        throw new Error(`Duplicate ID: ${id}. IDs must be unique across all objects in options.`);
                    }
                    usedIDs[id] = true;
                    return deepAssign(yAxis, { ...presetOptions.yAxis[0], ...yAxis, id });
                }) || presetOptions.yAxis
            });

            if (nextOptions?.chart?.type === 'pie') {
                nextOptions = deepAssign(nextOptions, {
                    chart: {
                        events: {
                            render: this._pieNegativeValueHoverHack,
                            addSeries: this._pieNegativeValueHoverHack
                        }
                    }
                });
            }
        }

        this.setState({ options: this._replaceReactComponentsData(nextOptions, '', null, false, false, oldComponents, newComponents) });

        for (var path in oldComponents) {
            if (oldComponents.hasOwnProperty(path) && !newComponents[path] && !newComponents[oldComponents[path].commonPath] && oldComponents[path].component) {
                if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Component removed from options, unmounting.'); }
                oldComponents[path].observer.disconnect();
                ReactDOM.unmountComponentAtNode(oldComponents[path].container);
                if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Unmounted.'); }
            } else if (oldComponents.hasOwnProperty(path) && !newComponents[path] && newComponents[oldComponents[path].commonPath]) {
                newComponents[path] = oldComponents[path];
            }
        }

        this._components = newComponents;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !deepEqual(this.state.options, nextState.options) || this.props !== nextProps;
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this._chart && this.state.options) {
            this.componentDidMount();

            for (let path in this._components) {
                if (this._components[path] && this._components[path].useLessShittyHTML && this._components[path].container) {
                    const visibleNode = document.getElementById(this._id + path.replace(/\./g, '__'));
                    if (visibleNode && visibleNode !== this._components[path].container) {
                        visibleNode.parentNode.insertBefore(this._components[path].container, visibleNode);
                        visibleNode.parentNode.removeChild(visibleNode);
                        this._components[path].observer.disconnect();
                    }
                }
            }
        } else if (this._chart && this.state.options) {
            for (let path in this._components) {
                if (this._components.hasOwnProperty(path)) {
                    this._components[path].rendered = false;
                }
            }

            const changes = deepDiff(prevState.options, this.state.options);
            if (changes) {
                if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Updating chart: ', changes); }

                this._isNestedComponentUpdate = deepEqual(prevProps, this.props);

                let hoverSeriesIndex = this._chart.series.indexOf(this._chart.hoverSeries),
                    hoverPointIndex = this._chart.series[hoverSeriesIndex] && this._chart.series[hoverSeriesIndex].data.indexOf(this._chart.hoverPoint),
                    tooltipIsHidden = !this._chart.tooltip || this._chart.tooltip.isHidden;

                if (changes.hasOwnProperty('xAxis')) {
                    changes.xAxis = changes.xAxis
                        .map((xAxisChange, index) => {
                            const
                                newXAxis = this.state.options.xAxis && this.state.options.xAxis[index],
                                id = newXAxis && newXAxis.id,
                                oldXAxis = id && prevState.options.xAxis && prevState.options.xAxis.find(s => s.id === id);

                            if (newXAxis && oldXAxis) {
                                // deepDiff compares array items by index so if some item is removed or inserted to the begginning of the array, all other will have a huge set of changes
                                // so we have to map them by id and calculate actual changes
                                xAxisChange = deepDiff(oldXAxis, newXAxis);
                            }
                            if (newXAxis && xAxisChange && oldXAxis) {
                                if (xAxisChange.hasOwnProperty('categories')) {
                                    xAxisChange.categories = newXAxis.categories;
                                }

                                if (xAxisChange.hasOwnProperty('plotBands')) {
                                    xAxisChange.plotBands = newXAxis.plotBands;
                                }

                                if (xAxisChange.hasOwnProperty('plotLines')) {
                                    xAxisChange.plotLines = newXAxis.plotLines;
                                }

                                xAxisChange.id = id;
                                return xAxisChange;
                            }
                            return null;
                        })
                        .filter(xAxis => xAxis && Object.keys(xAxis).length > 1);

                    if (!changes.xAxis.length) {
                        delete changes.xAxis;
                    }
                }

                if (changes.hasOwnProperty('yAxis')) {
                    changes.yAxis = changes.yAxis
                        .map((yAxisChange, index) => {
                            const
                                newYAxis = this.state.options.yAxis && this.state.options.yAxis[index],
                                id = newYAxis && newYAxis.id,
                                oldYAxis = id && prevState.options.yAxis && prevState.options.yAxis.find(s => s.id === id);

                            if (newYAxis && oldYAxis) {
                                // deepDiff compares array items by index so if some item is removed or inserted to the begginning of the array, all other will have a huge set of changes
                                // so we have to map them by id and calculate actual changes
                                yAxisChange = deepDiff(oldYAxis, newYAxis);
                            }
                            if (newYAxis && yAxisChange && oldYAxis) {
                                if (yAxisChange.hasOwnProperty('stops')) {
                                    yAxisChange.stops = newYAxis.stops;
                                }
                                if (yAxisChange.hasOwnProperty('plotBands')) {
                                    yAxisChange.plotBands = newYAxis.plotBands;
                                }

                                if (yAxisChange.hasOwnProperty('plotLines')) {
                                    yAxisChange.plotLines = newYAxis.plotLines;
                                }

                                if (yAxisChange.hasOwnProperty('tickPositions')) {
                                    yAxisChange.tickPositions = newYAxis.tickPositions;
                                }

                                yAxisChange.id = id;
                                return yAxisChange;
                            }
                            return null;
                        })
                        .filter(yAxis => yAxis && Object.keys(yAxis).length > 1);

                    if (!changes.yAxis.length) {
                        delete changes.yAxis;
                    }
                }

                if (changes.hasOwnProperty('series')) {
                    changes.series = changes.series.map((serieChange, index) => {
                        const newSerie = this.state.options.series[index],
                            id = newSerie && newSerie.id,
                            oldSerie = id && prevState.options.series.find(s => s.id === id);
                        if (newSerie && oldSerie) {
                            // deepDiff compares array items by index so if some item is removed or inserted to the begginning of the array, all other will have a huge set of changes
                            // so we have to map them by id and calculate actual changes
                            serieChange = deepDiff(oldSerie, newSerie);
                        }
                        if (newSerie && serieChange && oldSerie) {
                            if (serieChange.hasOwnProperty('zones')) {
                                serieChange.zones = newSerie.zones;
                            }
                            // Highcharts disables series' animations if something other than data changes
                            // update only non-data props with the first redraw, then update data with the second one
                            delete serieChange.data;
                            serieChange.id = id;
                            return serieChange;
                        }
                        return null;
                    }).filter(serie => serie && Object.keys(serie).length > 1);

                    if (!changes.series.length) {
                        delete changes.series;
                    }
                }

                const oldXAxes = prevState.options.xAxis || [];
                const newXAxes = this.state.options.xAxis || [];
                let xAxesChanged = false;

                const oldYAxes = prevState.options.yAxis || [];
                const newYAxes = this.state.options.yAxis || [];
                let yAxesChanged = false;

                newXAxes.forEach(newXAxis => {
                    if (!oldXAxes.some(oldXAxis => oldXAxis.id === newXAxis.id)) {
                        this._chart.addAxis(newXAxis, true, false);
                        xAxesChanged = true;
                    }
                });

                newYAxes.forEach(newYAxis => {
                    if (!oldYAxes.some(oldYAxis => oldYAxis.id === newYAxis.id)) {
                        this._chart.addAxis(newYAxis, false, false);
                        yAxesChanged = true;
                    }
                });

                // TODO do same stuff for all array properties - HC only updates existing items mapped by id or index, but does not remove or create them if count changes
                const oldSeries = prevState.options.series.slice();
                const newSeries = this.state.options.series;
                let seriesChanged = false;

                oldSeries.forEach(oldSerie => {
                    if (!newSeries.some(newSerie => oldSerie.id === newSerie.id)) {

                        this._chart.get(oldSerie.id).remove(false);
                        seriesChanged = true;
                    }
                });

                // some hacks for better performance in very specific cases below
                if (deepEqual(Object.keys(changes), ['tooltip']) && deepEqual(Object.keys(changes.tooltip), ['formatter'])) {
                    this._chart.options.tooltip = deepAssign(this._chart.options.tooltip, changes);
                }
                // some hacks for better performance in very specific cases above
                else {
                    this._chart.update(changes);
                }

                newSeries.forEach(newSerie => {
                    const oldSerie = oldSeries.find(oldSerie => oldSerie.id === newSerie.id),
                        dataDiff = oldSerie ? deepDiff(oldSerie.data, newSerie.data) : null;
                    if (oldSerie && dataDiff) {
                        const oldPoints = oldSerie.data.slice(),
                            newPoints = newSerie.data;
                        if (
                            // https://api.highcharts.com/highcharts/plotOptions.series.cropThreshold
                            // points with index bigger than cropThreshold are not created and can not be removed targetly (the only way is to call 'remove' of an instance of Point)
                            oldPoints.length < 300 && newPoints < 300 &&
                            (this._isDetailedPoints(oldPoints) || !oldPoints.length) && (this._isDetailedPoints(newPoints) || !newPoints.length) &&
                            (
                                oldPoints.some(oldPoint => !newPoints.some(newPoint => oldPoint.id === newPoint.id)) ||
                                newPoints.some(newPoint => !oldPoints.some(oldPoint => oldPoint.id === newPoint.id))
                            )) {
                            oldPoints.forEach(oldPoint => {
                                if (!newPoints.some(newPoint => oldPoint.id === newPoint.id)) {
                                    this._chart.get(oldPoint.id).remove(false);
                                }
                            });

                            newPoints.forEach(newPoint => {
                                const oldPoint = oldPoints.find(oldPoint => oldPoint.id === newPoint.id);
                                if (oldPoint && deepDiff(oldPoint, newPoint)) {
                                    this._chart.get(oldPoint.id).update(newPoint, false);
                                }
                                if (!oldPoint) {
                                    this._chart.get(oldSerie.id).addPoint(newPoint, false);
                                }
                            });
                        }
                        else {
                            this._chart.get(oldSerie.id).setData(newSerie.data, false);
                        }
                        seriesChanged = true;
                    }
                    if (!oldSerie) {
                        this._chart.addSeries(newSerie, false);
                        seriesChanged = true;
                    }
                });

                oldXAxes.forEach(oldXAxis => {
                    var newXAxis = newXAxes.find(newXAxis => oldXAxis.id === newXAxis.id);
                    if (!newXAxis) {
                        this._chart.get(oldXAxis.id).remove(false);
                        xAxesChanged = true;
                    }
                    else if (oldXAxis.min !== newXAxis.min || oldXAxis.max !== oldXAxis.max) {
                        this._chart.get(oldXAxis.id).setExtremes(undefined, undefined, false, false);
                        xAxesChanged = true;
                    }
                });

                oldYAxes.forEach(oldYAxis => {
                    if (!newYAxes.some(newYAxis => oldYAxis.id === newYAxis.id)) {
                        this._chart.get(oldYAxis.id).remove(false);
                        yAxesChanged = true;
                    }
                });

                if (seriesChanged || xAxesChanged || yAxesChanged) {
                    this._chart.redraw();
                    hoverSeriesIndex = hoverPointIndex = -1;
                }

                if (hoverSeriesIndex !== -1) {
                    (this._chart.hoverSeries = this._chart.series[hoverSeriesIndex]).setState('hover');

                    if (hoverPointIndex !== -1) {
                        (this._chart.hoverPoint = this._chart.series[hoverSeriesIndex].data[hoverPointIndex]).setState('hover');

                        if (!tooltipIsHidden) {
                            this._chart.tooltip.refresh(this._chart.series[hoverSeriesIndex].data[hoverPointIndex]);
                        }
                    }
                }

                this._isNestedComponentUpdate = false;
            }

            if (prevProps !== this.props) {
                // changes came from outside, parent component might have changed it's size, chart has to fit
                this._onWindowResize();
            }

            // some formatters in Highcharts can be used to format strings for multiple things called with different parameters
            // if some formatter returns React component we have to store separate instance per parameter set
            // otherwise we'll clone the same value i. e. for each axis tick, the one called the last
            // but since in the end formatter has to return plain string to Highcharts, we can't track this string's destiny
            // and for example drop a component when tooltip is not visible anymore (of course it's easy for tooltip, but not in general)
            // this produces quite many unused components when user interacts with the chart and some things appear and disappear
            // which is especially bad if they do some background job such as requesting updates data
            // TODO find a way to detect when component spawned by formatter is not in use anymore
            // for now we assume that if we update formatter in chart options, it should be invoked for all currently visible objects
            // so if we changed it, but it wasn't called, we can drop all related components
            // the code won't be invoked if i. e. tooltip becomes hidden because of HC event and not options change
            // but it will if changes come from within of some component
            // and if component doesn't do anything, it's not a big problem to have it in memory even if it's unused
            for (let path in this._components) {
                if (
                    this._components.hasOwnProperty(path) && getByPath(changes, this._components[path].commonPath) &&
                    this._components[path].renderer && !this._components[path].rendered && this._components[path].component
                ) {
                    if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Formatter changed, but related component wasn\'t used during update, unmounting.'); }
                    this._components[path].observer.disconnect();
                    ReactDOM.unmountComponentAtNode(this._components[path].container);
                    delete this._components[path];
                    if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Unmounted.'); }
                }
                else if (this._components[path] && this._components[path].useLessShittyHTML && this._components[path].container) {
                    const visibleNode = document.getElementById(this._id + path.replace(/\./g, '__'));
                    if (visibleNode && visibleNode !== this._components[path].container) {
                        visibleNode.parentNode.insertBefore(this._components[path].container, visibleNode);
                        visibleNode.parentNode.removeChild(visibleNode);
                        this._components[path].observer.disconnect();
                        // TODO do Highcharts keep reference to the element or just replace innerHTML? handle node removal?
                    }
                }
            }

            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Updated.'); }
        }
        else if (this._chart && !this.state.options) {
            this.componentWillUnmount();
        }
    }

    componentWillUnmount() {
        if (this._reactComponentUpdateBatchTimeoutID) {
            clearTimeout(this._reactComponentUpdateBatchTimeoutID);
            this._reactComponentUpdateBatchTimeoutID = null;
        }
        if (this._chart) {
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Destroying chart.'); }
            window.removeEventListener('resize', this._onWindowResize);
            this._chart.destroy();
            this._chart = null;
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Destroyed.'); }
        }
        for (let path in this._components) {
            if (this._components.hasOwnProperty(path) && this._components[path].component) {
                if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Chart is destroyed, unmounting nested component.'); }
                this._components[path].observer.disconnect();
                ReactDOM.unmountComponentAtNode(this._components[path].container);
                if (DEBUG && LOGGING_ENABLED) { log(this, path, 'Unmounted.'); }
            }
        }
    }

    _isDetailedPoints(data) {
        return data && data[0] && typeof data[0] === 'object' && !Array.isArray(data[0]);
    }

    _onWindowResize = () => {
        if (this._chart) {
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Window size changed, recalculating chart sizes.'); }
            this._chart.reflow();
            if (DEBUG && LOGGING_ENABLED) { log(this, '', 'Recalculated.'); }
        }
    }

    _pieNegativeValueHoverHack(chart, _event) {
        try {
            chart.series.forEach((serie) => {
                if (!serie.halo && serie.points.some(point => point && !point.graphic)) {
                    const hoverablePoint = serie.points.find(point => point && !point.isNull && point.graphic);

                    if (hoverablePoint) {
                        hoverablePoint.setState('hover');
                        hoverablePoint.setState('normal');
                    }
                }
            });
        }
        catch {
            // Ignored since it's only purpose is to suppress a very specific error
        }
    }

    _downloadChartImage = (imageName) => {
        if (this._chart) {
            this._chart.exportChartLocal({
                type: 'image/png',
                filename: imageName || 'chart'
            });
        }
    }

    _getChartSvg = (width, height) => {
        if (!this._chart) {
            return null;
        }

        if (width && height) {
            return this._chart.getSVG({
                chart: {
                    width,
                    height
                }
            });
        }

        return this._chart.getSVG();
    }

    _getChartWidth = () => {
        return this._chart ? this._chart.chartWidth : null;
    }

    _getChartHeight = () => {
        return this._chart ? this._chart.chartHeight : null;
    }

    render() {
        return (<div id={this._id} ref="container" className={'chart ' + this.props.className} />);
    }
}


